import { useState } from 'react';
import { Container, Typography, Box, Alert, Snackbar } from '@mui/material';
import BasePage from '../BasePage';
import AdminLayout from '../../components/AdminLayout';
import UserFilters from '../../components/admin/users/UserFilters';
import UsersTable from '../../components/admin/users/UsersTable';
import UserDetailModal from '../../components/admin/users/UserDetailModal';
import BanUserModal from '../../components/admin/users/BanUserModal';
import DeleteUserModal from '../../components/admin/users/DeleteUserModal';
import { useAdminUsers } from '../../hooks/admin/useAdminUsers';
import { AdminUser } from '../../types/admin';

/**
 * AdminUsers - User management page
 *
 * Section 2: Users
 * - User search and filtering
 * - User list table with pagination
 * - User detail drawer (slide-out from right)
 * - Ban/unban user functionality with reason input
 * - Delete user functionality with typed confirmation
 * - User activity timeline in detail view
 *
 * Features:
 * - Comprehensive filtering (tier, status, type, search)
 * - Active filter chips display
 * - Inline actions (View, Ban/Unban, Delete)
 * - Drawer-based detail view (maintains table context)
 * - Tabbed user details (Overview, Activity, Homes, Relationships, Moderation)
 * - Confirmation modals for dangerous actions
 * - Real-time user count display
 * - Toast notifications for action feedback
 */
export default function AdminUsers() {
  const {
    users,
    totalUsers,
    isLoading,
    error,
    pagination,
    filters,
    searchUsers,
    filterUsers,
    clearFilters,
    setPagination,
    banUser,
    unbanUser,
    deleteUser,
    getUserDetail,
    selectedUserId,
    setSelectedUserId,
  } = useAdminUsers();

  // Modal states
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToModerate, setUserToModerate] = useState<AdminUser | null>(null);

  // Toast notification state
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Handlers for table actions
  const handleViewUser = (user: AdminUser) => {
    setSelectedUserId(user.id);
  };

  const handleBanUser = (user: AdminUser) => {
    setUserToModerate(user);
    setBanModalOpen(true);
  };

  const handleUnbanUser = async (user: AdminUser) => {
    const result = await unbanUser(user.id);
    if (result.success) {
      setToast({
        open: true,
        message: `User ${user.name} has been unbanned successfully`,
        severity: 'success',
      });
      // Refresh selected user detail if open
      if (selectedUserId === user.id) {
        setSelectedUserId(null);
        setTimeout(() => setSelectedUserId(user.id), 100);
      }
    } else {
      setToast({
        open: true,
        message: result.error || 'Failed to unban user',
        severity: 'error',
      });
    }
  };

  const handleDeleteUser = (user: AdminUser) => {
    setUserToModerate(user);
    setDeleteModalOpen(true);
  };

  // Ban modal confirmation
  const handleBanConfirm = async (userId: string, reason: string) => {
    const result = await banUser(userId, reason);
    if (result.success) {
      setBanModalOpen(false);
      setToast({
        open: true,
        message: `User ${userToModerate?.name} has been banned successfully`,
        severity: 'success',
      });
      // Close detail drawer if this user was being viewed
      if (selectedUserId === userId) {
        setSelectedUserId(null);
      }
      setUserToModerate(null);
    } else {
      setToast({
        open: true,
        message: result.error || 'Failed to ban user',
        severity: 'error',
      });
    }
    return result;
  };

  // Delete modal confirmation
  const handleDeleteConfirm = async (userId: string) => {
    const result = await deleteUser(userId);
    if (result.success) {
      setDeleteModalOpen(false);
      setToast({
        open: true,
        message: `User ${userToModerate?.name} has been deleted permanently`,
        severity: 'success',
      });
      // Close detail drawer if this user was being viewed
      if (selectedUserId === userId) {
        setSelectedUserId(null);
      }
      setUserToModerate(null);
    } else {
      setToast({
        open: true,
        message: result.error || 'Failed to delete user',
        severity: 'error',
      });
    }
    return result;
  };

  // Detail drawer moderation actions
  const handleDetailBan = () => {
    if (!selectedUserId) return;
    const user = users.find((u) => u.id === selectedUserId);
    if (user) {
      handleBanUser(user);
    }
  };

  const handleDetailUnban = async () => {
    if (!selectedUserId) return;
    const user = users.find((u) => u.id === selectedUserId);
    if (user) {
      await handleUnbanUser(user);
    }
  };

  const handleDetailDelete = () => {
    if (!selectedUserId) return;
    const user = users.find((u) => u.id === selectedUserId);
    if (user) {
      handleDeleteUser(user);
    }
  };

  return (
    <BasePage
      title="User Management | CribCritic Admin"
      description="Manage users"
    >
      <AdminLayout>
        <Container maxWidth="xl">
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              User Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Search, view, and moderate users • {totalUsers.toLocaleString()} users found
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <UserFilters
            filters={filters}
            onSearchChange={searchUsers}
            onFilterChange={filterUsers}
            onClearFilters={clearFilters}
          />

          <UsersTable
            users={users}
            isLoading={isLoading}
            error={error}
            pagination={pagination}
            onPageChange={(page) => setPagination({ ...pagination, page })}
            onLimitChange={(limit) => setPagination({ ...pagination, limit, page: 1 })}
            onViewUser={handleViewUser}
            onBanUser={handleBanUser}
            onUnbanUser={handleUnbanUser}
            onDeleteUser={handleDeleteUser}
          />

          {/* User Detail Drawer */}
          <UserDetailModal
            open={!!selectedUserId}
            userId={selectedUserId}
            getUserDetail={getUserDetail}
            onClose={() => setSelectedUserId(null)}
            onBanUser={handleDetailBan}
            onUnbanUser={handleDetailUnban}
            onDeleteUser={handleDetailDelete}
          />

          {/* Ban User Modal */}
          <BanUserModal
            open={banModalOpen}
            user={userToModerate}
            onConfirm={handleBanConfirm}
            onCancel={() => {
              setBanModalOpen(false);
              setUserToModerate(null);
            }}
            isLoading={isLoading}
          />

          {/* Delete User Modal */}
          <DeleteUserModal
            open={deleteModalOpen}
            user={userToModerate}
            onConfirm={handleDeleteConfirm}
            onCancel={() => {
              setDeleteModalOpen(false);
              setUserToModerate(null);
            }}
            isLoading={isLoading}
          />

          {/* Toast Notifications */}
          <Snackbar
            open={toast.open}
            autoHideDuration={6000}
            onClose={() => setToast({ ...toast, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setToast({ ...toast, open: false })}
              severity={toast.severity}
              sx={{ width: '100%' }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Container>
      </AdminLayout>
    </BasePage>
  );
}
